import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router';
import { describeError } from '../lib/api';
import {
  sessionKey,
  useAttendee,
  useConsumeSignInLink,
  useSignInResult,
  type SignInChoice,
} from '../lib/queries';

/**
 * Where the emailed link lands. It spends the token immediately, and then does one of three things:
 * lands on the home screen, asks which of the people registered under that address is signing in
 * (ACC-04), or explains why the link no longer works (ACC-06).
 *
 * The choice is asked here rather than on the sign-in form deliberately: by this point they have
 * proved they can read that inbox, so a list of the two names on it tells them nothing they did not
 * already know. Asking earlier would have made the form a way to enumerate who is attending.
 */
export function ConsumeSignInPage() {
  const { token = '' } = useParams();
  const consume = useConsumeSignInLink();
  const { mutate } = consume;
  const client = useQueryClient();
  // SessionProvider already asked /auth/me. If consume fails after a remount spent the token,
  // that cookie is usually already set — refetch before telling them the link is dead.
  const existing = useAttendee();
  const cached = useSignInResult(token);

  useEffect(() => {
    // Wait for `/auth/me` the way the console does: StrictMode drops the mutation observer
    // between effect passes, and a `choose_person` answer has no cookie to recover from.
    if (!token || existing.data || existing.isPending || cached.data || consume.isError) {
      return;
    }
    mutate({ token });
  }, [cached.data, consume.isError, existing.data, existing.isPending, mutate, token]);

  useEffect(() => {
    if (!consume.isError) {
      return;
    }
    void client.invalidateQueries({ queryKey: sessionKey });
  }, [client, consume.isError]);

  const result = consume.data ?? cached.data;

  if (result?.status === 'signed_in' || existing.data) {
    return <Navigate to="/" replace />;
  }

  if (consume.isError && !existing.isPending && !existing.isFetching) {
    return (
      <Frame title="That link did not work">
        <p className="text-body mt-2 text-ink-3">
          {describeError(
            consume.error,
            'This sign-in link has expired or has already been used. Request a new one.',
          )}
        </p>
        <Link
          to="/"
          replace
          className="text-button rounded-button mt-5 flex min-h-touch items-center justify-center bg-ink px-4 font-semibold text-white"
        >
          Send a new link
        </Link>
      </Frame>
    );
  }

  if (result?.status === 'choose_person') {
    return (
      <Frame title="Who is signing in?">
        <p className="text-body mt-2 text-ink-3">
          More than one registration uses this email address. Pick yours — the app remembers your
          choice on this device.
        </p>

        <ul className="mt-5 flex flex-col gap-card">
          {result.choices.map((choice) => (
            <li key={choice.id}>
              <ChoiceButton
                choice={choice}
                disabled={consume.isPending}
                onSelect={() => consume.mutate({ token, personId: choice.id })}
              />
            </li>
          ))}
        </ul>

        <p className="text-subtitle mt-4 text-ink-4">
          Signing in as someone else later means asking for a new link.
        </p>
      </Frame>
    );
  }

  return (
    <Frame title="Signing you in…">
      <p className="text-body mt-2 text-ink-3">One moment.</p>
    </Frame>
  );
}

function ChoiceButton({
  choice,
  disabled,
  onSelect,
}: {
  choice: SignInChoice;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      className="rounded-card min-h-row flex w-full flex-col items-start justify-center border border-border-input bg-surface px-4 py-3 text-left disabled:opacity-60"
    >
      <span className="text-row font-semibold text-ink">{choice.name}</span>
      {/* The company is what tells two attendees with the same first name apart. */}
      <span className="text-subtitle mt-0.5 text-ink-3">
        {choice.company ? `${choice.company} · ${choice.category}` : choice.category}
      </span>
    </button>
  );
}

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="px-gutter flex min-h-dvh flex-col pt-12 pb-6">
      <img
        src="/logo-lockup.png"
        alt="Go For The Greens"
        className="h-auto w-[164px] max-w-full"
        width={400}
        height={275}
      />
      <h1 className="font-display text-session-title mt-6 font-semibold text-ink">{title}</h1>
      {children}
    </main>
  );
}
